/**
 * Client API pour les workers auth (8787) et admin (8788).
 * Toutes les requêtes incluent credentials (cookie JWT httpOnly).
 */
const AUTH_BASE = import.meta.env.VITE_AUTH_URL ?? 'http://localhost:8787';
const ADMIN_BASE = import.meta.env.VITE_ADMIN_URL ?? 'http://localhost:8788';
const SUPPORT_BASE = import.meta.env.VITE_SUPPORT_URL ?? 'http://localhost:8792';

async function request<T>(base: string, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error((data as { error?: string }).error ?? `http_${res.status}`);
    (err as Error & { status?: number; data?: unknown }).status = res.status;
    (err as Error & { status?: number; data?: unknown }).data = data;
    throw err;
  }
  return data as T;
}

export const authApi = {
  me: () => request<{ ok: true; user: { id: string; email: string; role: string } }>(AUTH_BASE, '/auth/me'),
  login: (email: string, password: string) =>
    request<{ ok: true; user: { id: string; email: string; role: string } }>(AUTH_BASE, '/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: true }>(AUTH_BASE, '/auth/logout', { method: 'POST' }),
};

export const adminApi = {
  metrics: () => request<{ ok: true; data: import('@reims-local/types').AdminMetricsResponse }>(
    ADMIN_BASE,
    '/admin/metrics',
  ),
  listShops: (params: { status?: string; shop_type?: string; plan?: string; search?: string } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
    const q = qs.toString();
    return request<{ ok: true; data: import('@reims-local/types').Shop[] }>(
      ADMIN_BASE,
      `/admin/shops${q ? `?${q}` : ''}`,
    );
  },
  getShop: (id: string) =>
    request<{
      ok: true;
      data: {
        shop: import('@reims-local/types').Shop;
        invitations: import('@reims-local/types').ShopInvitation[];
      };
    }>(ADMIN_BASE, `/admin/shops/${id}`),
  createShop: (input: import('@reims-local/types').CreateShopInput) =>
    request<{ ok: true; data: { id: string; slug: string } }>(ADMIN_BASE, '/admin/shops', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  importShops: (csv: string) =>
    request<{ ok: true; data: import('@reims-local/types').ImportShopResult }>(
      ADMIN_BASE,
      '/admin/shops/import',
      {
        method: 'POST',
        body: JSON.stringify({ csv }),
      },
    ),
  sendInvitation: (shopId: string) =>
    request<{ ok: true; data: import('@reims-local/types').SendInvitationResult }>(
      ADMIN_BASE,
      `/admin/shops/${shopId}/invitation`,
      { method: 'POST' },
    ),
  listInvitations: (status?: string) => {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    return request<{
      ok: true;
      data: Array<{
        invitation: import('@reims-local/types').ShopInvitation;
        shop: import('@reims-local/types').Shop;
      }>;
    }>(ADMIN_BASE, `/admin/invitations${q}`);
  },
  getAnalysis: () =>
    request<{ ok: true; data: import('@reims-local/types').AnalysisEnseigne[] }>(
      ADMIN_BASE,
      '/admin/analysis/enseignes',
    ),
};

type SupportTicketRow = {
  ticket: import('@reims-local/types').SupportTicket;
  shop: { id: string; name: string; slug: string; plan: string };
};

export const supportApi = {
  list: (params: { status?: string; category?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.category) qs.set('category', params.category);
    const q = qs.toString();
    return request<{ ok: true; data: SupportTicketRow[] }>(
      SUPPORT_BASE,
      `/support/admin/tickets${q ? `?${q}` : ''}`,
    );
  },
  get: (id: string) =>
    request<{
      ok: true;
      data: {
        ticket: import('@reims-local/types').SupportTicket;
        shop: {
          id: string; name: string; slug: string; plan: string; status: string;
          shop_type: string; created_at: string; email: string | null;
        } | null;
        messages: import('@reims-local/types').SupportMessage[];
        merchant: { id: string; email: string; name: string | null; phone: string | null } | null;
      };
    }>(SUPPORT_BASE, `/support/admin/tickets/${id}`),
  reply: (id: string, content: string) =>
    request<{ ok: true }>(SUPPORT_BASE, `/support/admin/tickets/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),
  patch: (id: string, body: {
    status?: import('@reims-local/types').SupportStatus;
    category?: import('@reims-local/types').SupportCategory;
    priority?: 1 | 2 | 3 | 4;
    admin_note?: string | null;
  }) =>
    request<{ ok: true; data: import('@reims-local/types').SupportTicket }>(
      SUPPORT_BASE,
      `/support/admin/tickets/${id}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
};
